/**
 * Browser Detection Utilities
 * สำหรับตรวจจับ LINE browser, Facebook Messenger และเปิดใน browser ภายนอก
 */

/**
 * ตรวจจับว่าเปิดจาก LINE browser หรือไม่
 * @returns {boolean}
 */
export const isLineBrowser = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const ua = userAgent.toLowerCase();
  
  // ตรวจสอบ LINE browser จาก user agent
  // LINE browser มักจะมี "Line" หรือ "LINE" ใน user agent
  return ua.includes('line') || 
         ua.includes('naver') || // LINE ใช้ Naver browser engine บางครั้ง
         (ua.includes('android') && ua.includes('wv') && ua.includes('line')); // Android WebView (LINE)
};

/**
 * ตรวจจับว่าเปิดจาก Facebook Messenger (แชทเฟจ) หรือไม่
 * @returns {boolean}
 */
export const isFacebookBrowser = () => {
  if (typeof window === 'undefined') return false;
  
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const ua = userAgent.toLowerCase();
  
  // ตรวจสอบ Facebook Messenger browser จาก user agent
  // Facebook Messenger มักจะมี "FBAN" หรือ "FBAV" ใน user agent
  // หรือ "Messenger" ใน user agent
  return ua.includes('fban') || 
         ua.includes('fbav') || 
         ua.includes('messenger') ||
         (ua.includes('android') && ua.includes('wv') && (ua.includes('facebook') || ua.includes('fban'))) ||
         // iOS Facebook Messenger
         (ua.includes('iphone') && ua.includes('os_') && ua.includes('messenger')) ||
         // ตรวจสอบจาก referrer หรือ window.name
         (typeof window !== 'undefined' && (
           window.location.href.includes('messenger.com') ||
           document.referrer.includes('messenger.com') ||
           document.referrer.includes('facebook.com')
         ));
};

/**
 * ตรวจจับว่าเปิดจาก In-App Browser (LINE, Facebook Messenger) หรือไม่
 * @returns {boolean}
 */
export const isInAppBrowser = () => {
  return isLineBrowser() || isFacebookBrowser();
};

/**
 * รับชื่อ browser ที่เปิดอยู่
 * @returns {string} 'line' | 'facebook' | 'other'
 */
export const getInAppBrowserName = () => {
  if (isLineBrowser()) return 'line';
  if (isFacebookBrowser()) return 'facebook';
  return 'other';
};

/**
 * ตรวจจับว่าเป็น mobile device หรือไม่
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  if (typeof window === 'undefined') return false;
  
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

/**
 * ตรวจจับว่าเป็น Android หรือไม่
 * @returns {boolean}
 */
export const isAndroid = () => {
  if (typeof window === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
};

/**
 * ตรวจจับว่าเป็น iOS หรือไม่
 * @returns {boolean}
 */
export const isIOS = () => {
  if (typeof window === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

/**
 * เปิด URL ใน browser ภายนอก
 * @param {string} url - URL ที่ต้องการเปิด
 * @returns {boolean} - true ถ้าสามารถเปิดได้, false ถ้าไม่สามารถเปิดได้
 */
export const openInExternalBrowser = (url = window.location.href) => {
  if (typeof window === 'undefined') return false;
  
  try {
    const currentUrl = url || window.location.href;
    
    // ป้องกันการ redirect ซ้ำๆ โดยตรวจสอบว่าเคยพยายาม redirect ใน session นี้แล้วหรือยัง
    const redirectKey = `redirect_attempt_${currentUrl}`;
    const lastRedirectAttempt = sessionStorage.getItem(redirectKey);
    const now = Date.now();
    
    // ถ้าเคยพยายาม redirect ใน 5 วินาทีที่ผ่านมา ให้หยุด (ป้องกัน loop)
    if (lastRedirectAttempt && (now - parseInt(lastRedirectAttempt, 10)) < 5000) {
      console.log('Preventing redirect loop - recent attempt detected');
      return false;
    }
    
    // บันทึกเวลาที่พยายาม redirect
    sessionStorage.setItem(redirectKey, now.toString());
    
    if (isAndroid()) {
      // สำหรับ Android: ใช้ Intent URL เพื่อเปิดใน default browser
      try {
        // สร้าง Intent URL สำหรับ Android
        // Format: intent://[host][path]#Intent;scheme=[scheme];package=[package];end
        const urlObj = new URL(currentUrl);
        const host = urlObj.host;
        const path = urlObj.pathname + urlObj.search + urlObj.hash;
        
        // ใช้ Intent URL เพื่อเปิดใน default browser
        const intentUrl = `intent://${host}${path}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
        
        // ลองเปิดด้วย Intent ก่อน
        window.location.href = intentUrl;
        
        // ถ้า Intent ไม่ทำงาน (ไม่มี app ที่รองรับ) ให้ fallback
        // ใช้ setTimeout เพื่อรอให้ Intent ทำงานก่อน
        setTimeout(() => {
          // ถ้ายังอยู่ใน LINE browser แสดงว่า Intent ไม่ทำงาน
          // ให้ใช้วิธีอื่น: สร้าง hidden link และคลิก
          const link = document.createElement('a');
          link.href = currentUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }, 1000);
      } catch (e) {
        // Fallback: ใช้ window.open() หรือสร้าง link
        try {
          window.open(currentUrl, '_blank', 'noopener,noreferrer');
        } catch (e2) {
          // ถ้ายังไม่ได้ ให้สร้าง link และคลิก
          const link = document.createElement('a');
          link.href = currentUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    } else if (isIOS()) {
      // สำหรับ iOS: ใช้หลายวิธีเพื่อให้เปิด Browser ได้แน่นอน
      // iPhone 8 และ iOS เก่าอาจมีปัญหาเปิดจาก LINE browser
      try {
        // สำหรับ public pages (/nurse/home, /) ไม่ต้อง redirect เพราะเป็นเว็บให้ข้อมูล
        // ใช้งานได้ดีใน In-App Browser
        const currentPath = new URL(currentUrl).pathname;
        const isPublicPage = currentPath === '/' || currentPath === '/nurse/home' || currentPath === '/nurse/landing';
        
        if (isPublicPage) {
          // สำหรับ public pages ไม่ต้อง redirect
          return false;
        }
        
        // วิธีที่ 1: ใช้ window.open() ก่อน (ปลอดภัยกว่า window.location.href)
        // เพราะ window.location.href จะทำให้หน้าเว็บ reload ซ้ำๆ
        if (isInAppBrowser()) {
          // ถ้าเปิดจาก LINE browser หรือ Facebook Messenger ให้ใช้ window.open()
          // เพื่อเปิดใน Safari โดยไม่ reload หน้าเดิม
          const opened = window.open(currentUrl, '_blank', 'noopener,noreferrer');
          if (opened) {
            return true;
          }
        } else {
          // ถ้าไม่ใช่ In-App browser ให้ใช้ window.open() ธรรมดา
          window.open(currentUrl, '_blank', 'noopener,noreferrer');
          return true;
        }
      } catch (e) {
        // Fallback 1: สร้าง link และคลิก (วิธีที่ทำงานได้ดีที่สุดบน iOS)
        try {
          const link = document.createElement('a');
          link.href = currentUrl;
          link.target = '_system'; // ใช้ _system สำหรับ iOS เพื่อเปิดใน Safari
          link.rel = 'noopener noreferrer';
          link.style.position = 'fixed';
          link.style.top = '0';
          link.style.left = '0';
          link.style.width = '100%';
          link.style.height = '100%';
          link.style.opacity = '0';
          link.style.zIndex = '999999';
          document.body.appendChild(link);
          
          // ใช้ setTimeout เพื่อให้แน่ใจว่า link ถูก append แล้ว
          setTimeout(() => {
            link.click();
            // ลบ link หลังจากคลิกแล้ว
            setTimeout(() => {
              if (link.parentNode) {
                document.body.removeChild(link);
              }
            }, 100);
          }, 50);
        } catch (e2) {
          // Fallback 2: ใช้ window.location.replace (สุดท้าย)
          try {
            window.location.replace(currentUrl);
          } catch (e3) {
            console.error('All methods failed to open browser:', e3);
            return false;
          }
        }
      }
    } else {
      // สำหรับ Desktop: ใช้ window.open() ธรรมดา
      window.open(currentUrl, '_blank', 'noopener,noreferrer');
    }
    
    return true;
  } catch (error) {
    console.error('Error opening external browser:', error);
    return false;
  }
};

/**
 * Copy URL ไปยัง clipboard
 * @param {string} url - URL ที่ต้องการ copy
 * @returns {Promise<boolean>} - true ถ้า copy สำเร็จ
 */
export const copyToClipboard = async (url = window.location.href) => {
  if (typeof window === 'undefined') return false;
  
  try {
    const currentUrl = url || window.location.href;
    
    // ใช้ Clipboard API ถ้ามี
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(currentUrl);
      return true;
    }
    
    // Fallback: ใช้วิธีเก่า (สร้าง textarea และ copy)
    const textArea = document.createElement('textarea');
    textArea.value = currentUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    try {
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      return successful;
    } catch (err) {
      document.body.removeChild(textArea);
      return false;
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};

/**
 * ตรวจสอบว่า user เคยเลือกไม่แสดง modal อีกหรือไม่
 * @returns {boolean}
 */
export const shouldShowBrowserPrompt = () => {
  if (typeof window === 'undefined') return true;
  
  const browserName = getInAppBrowserName();
  const dismissed = localStorage.getItem(`${browserName}_browser_prompt_dismissed`) || 
                    localStorage.getItem('line_browser_prompt_dismissed'); // Backward compatibility
  if (dismissed) {
    const dismissedTime = parseInt(dismissed, 10);
    const now = Date.now();
    // ถ้า dismiss ไปแล้ว 7 วัน ให้แสดงอีกครั้ง
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return (now - dismissedTime) > sevenDays;
  }
  
  return true;
};

/**
 * ตรวจสอบว่า user ไม่ต้องการ auto-redirect หรือไม่
 * @returns {boolean}
 */
export const shouldAutoRedirect = () => {
  if (typeof window === 'undefined') return true;
  
  const browserName = getInAppBrowserName();
  const disabled = localStorage.getItem(`${browserName}_browser_auto_redirect_disabled`) ||
                   localStorage.getItem('line_browser_auto_redirect_disabled'); // Backward compatibility
  return !disabled; // ถ้าไม่มีค่า = true (ให้ auto-redirect)
};

/**
 * บันทึกว่า user ไม่ต้องการเห็น prompt อีก
 */
export const dismissBrowserPrompt = () => {
  if (typeof window === 'undefined') return;
  const browserName = getInAppBrowserName();
  localStorage.setItem(`${browserName}_browser_prompt_dismissed`, Date.now().toString());
  // Backward compatibility
  if (browserName === 'line') {
    localStorage.setItem('line_browser_prompt_dismissed', Date.now().toString());
  }
};

/**
 * ปิดการใช้งาน auto-redirect (user ต้องการใช้ใน In-App Browser)
 */
export const disableAutoRedirect = () => {
  if (typeof window === 'undefined') return;
  const browserName = getInAppBrowserName();
  localStorage.setItem(`${browserName}_browser_auto_redirect_disabled`, 'true');
  // Backward compatibility
  if (browserName === 'line') {
    localStorage.setItem('line_browser_auto_redirect_disabled', 'true');
  }
};

/**
 * เปิดการใช้งาน auto-redirect อีกครั้ง
 */
export const enableAutoRedirect = () => {
  if (typeof window === 'undefined') return;
  const browserName = getInAppBrowserName();
  localStorage.removeItem(`${browserName}_browser_auto_redirect_disabled`);
  // Backward compatibility
  localStorage.removeItem('line_browser_auto_redirect_disabled');
};

